import { useCallback, useEffect, useRef, type ReactNode } from "react";
import { Loader2 } from "lucide-react";
import type {
  DeviceMediaItem,
  DeviceSync,
  SlotSignageContent,
} from "@workspace/api-client-react";
import { t } from "@workspace/strings/web";
import { onDeviceControlsLocked } from "@/lib/device-policy";
import { useDevice } from "@/player/context/device-context";
import {
  usePlayerRealtime,
  type CommandHandler,
  type NowPlayingPayload,
  type SignalMessage,
} from "@/player/hooks/use-player-realtime";
import {
  CACHE_REPORT_EVENT,
  useDeviceSync,
} from "@/player/hooks/use-device-sync";
import {
  type CacheReportMemo,
  cacheReportMemo,
  shouldSendCacheReport,
} from "@workspace/player-shell/cache-report";
import type { OfflineReadinessReport } from "@workspace/player-shell/offline-window";
import { usePlayback, type Playback } from "@/player/hooks/use-playback";
import { useBroadcastReceiver } from "@/player/hooks/use-broadcast-receiver";
import { applyPlayerCommand } from "@/player/lib/commands";
import { isOfflineCapablePlayer, isDesktopShell } from "@/lib/platform";
import { isPlayerSurface } from "@/lib/player-surface";
import { enqueueEvent } from "@/player/lib/offline-store";
import { flushTelemetryQueue } from "@/player/lib/telemetry-flush";
import type { QueueEntry } from "@workspace/player-core";
import { useKioskExit } from "@/player/hooks/kiosk-exit-context";
import { useKioskSecretExit } from "@/player/hooks/use-kiosk-secret-exit";
import { useWakeLock } from "@/player/hooks/use-wake-lock";
import { PlayerDebugPanel } from "@/player/components/player-debug-panel";
import { SignageCrossfade } from "@/player/components/signage-crossfade";
import { ZoneSignage } from "@/player/components/zone-signage";
import type { ZoneNowPlaying } from "@/player/components/widgets/now-playing";
import { resolveToneUrl } from "@/player/lib/announcement-tone";
import { readVideoFit, videoFitClass } from "@/player/lib/video-fit";
import {
  needsDisplayTransform,
  orientationContainerStyle,
  readOrientation,
  readScale,
  scaleContainerStyle,
  type DeviceOrientation,
} from "@/player/lib/display-transform";

/**
 * Rotation + zoom canvas from device display settings (orientation 0/90/180/
 * 270, scale 50–200%) — the same seam that delivers videoFit. 90/270 swap the
 * viewport dimensions so a physically rotated screen renders upright; the
 * defaults render children untouched (no extra wrappers on the common path).
 */
function DisplayTransform({
  orientation,
  scalePercent,
  children,
}: {
  orientation: DeviceOrientation;
  scalePercent: number;
  children: ReactNode;
}) {
  if (!needsDisplayTransform(orientation, scalePercent)) return <>{children}</>;
  return (
    <div
      className="fixed inset-0 overflow-hidden bg-background"
      data-testid="display-transform"
      data-orientation={orientation}
      data-scale={scalePercent}
    >
      <div style={orientationContainerStyle(orientation)}>
        {/* Flex column so the shell fills the (possibly dimension-swapped)
            canvas — its own min-h-[100dvh] floor is wrong after a 90° swap,
            hence the !important reset. */}
        <div
          className="flex size-full flex-col [&>*]:flex-1 [&>*]:min-h-0!"
          style={scaleContainerStyle(scalePercent)}
        >
          {children}
        </div>
      </div>
    </div>
  );
}

/** Invisible warm-up for the next signage item so rotation cuts are seamless. */
function SignagePreloader({
  next,
  current,
}: {
  next: DeviceMediaItem | null;
  current: DeviceMediaItem | null;
}) {
  const shouldPreload = Boolean(next && next.id !== current?.id);
  const url = shouldPreload ? next!.url : null;
  const type = shouldPreload ? next!.type : null;

  useEffect(() => {
    if (!url || type !== "image") return;
    const img = new Image();
    img.src = url;
  }, [url, type]);

  if (!url || type !== "video") return null;
  return (
    <video
      src={url}
      preload="auto"
      muted
      playsInline
      className="hidden"
      aria-hidden="true"
      data-testid="signage-preload"
    />
  );
}

/**
 * Blank kiosk canvas: full-bleed video/signage when scheduled; an audio-only
 * device deliberately shows NOTHING on screen (docs/device-roles.md — "audio
 * plays with no on-screen transport, artwork, or queue"). The canvas is
 * FORCED BLACK below: a venue screen glowing light-theme white while music
 * plays reads as a dead device — it is almost certainly the story behind the
 * earlier "kiosk not playing" report. Whether the blank canvas should instead
 * show a now-playing visual is an open owner decision.
 */
/** Map the on-air queue entries to what the now-playing widget renders. */
function toZoneNowPlaying(
  current: QueueEntry | null,
  upNext: QueueEntry | null,
  playing: boolean,
): ZoneNowPlaying {
  const info = (entry: QueueEntry | null) =>
    entry
      ? {
          title: entry.item.title,
          artist: entry.item.artist ?? null,
          artworkUrl: entry.item.artworkUrl ?? null,
          playlistName: entry.playlistName,
        }
      : null;
  return { current: info(current), upNext: info(upNext), playing };
}

function KioskPlayerShell({
  sync,
  current,
  upNext,
  isPlaying,
  scheduleEnabled,
  videoItem,
  signageContent,
  foregroundVideoItem,
  foregroundVideoMuted,
  screenPaused,
  fitClass,
  autoplayBlocked,
  stallWarning,
  playerDebug,
  setVideoElement,
  setForegroundVideoElement,
  reportScreenImageError,
  onAutoplayStart,
}: {
  sync: DeviceSync | null;
  current: QueueEntry | null;
  upNext: QueueEntry | null;
  isPlaying: boolean;
  scheduleEnabled: boolean;
  videoItem: DeviceMediaItem | null;
  signageContent: SlotSignageContent | null;
  foregroundVideoItem: DeviceMediaItem | null;
  foregroundVideoMuted: boolean;
  /** Screen bus paused → blank the signage (CB-143). */
  screenPaused: boolean;
  /** object-fit class from the device's videoFit setting. */
  fitClass: string;
  autoplayBlocked: boolean;
  stallWarning: string | null;
  playerDebug: {
    audioSnapshot: ReturnType<typeof import("@/player/lib/player-debug").snapshotAudioElement>;
    audioContextState: string | null;
    stallWarning: string | null;
  };
  setVideoElement: (el: HTMLVideoElement | null) => void;
  setForegroundVideoElement: (el: HTMLVideoElement | null) => void;
  reportScreenImageError: (surface: "signage" | "foreground") => void;
  onAutoplayStart: () => void;
}) {
  const signageWeb = Boolean(
    signageContent && !videoItem && !foregroundVideoItem,
  );
  const signageVideo = Boolean(videoItem && !foregroundVideoItem);
  return (
    <div
      className="relative min-h-[100dvh] w-full bg-black overflow-hidden"
      data-testid="kiosk-player-shell"
    >
      {/* Web page / widget tile: hard-cut, renders BEFORE the media crossfade
          path — live pages and widgets never blend. */}
      {signageWeb && signageContent && (
        <ZoneSignage
          content={signageContent}
          className="absolute inset-0 size-full z-10"
          nowPlaying={toZoneNowPlaying(current, upNext, isPlaying)}
        />
      )}

      {signageVideo && videoItem && (
        <SignageCrossfade
          item={videoItem}
          setVideoRef={setVideoElement}
          className={`absolute inset-0 size-full ${fitClass} z-10`}
          surface="signage"
          onImageError={() => reportScreenImageError("signage")}
        />
      )}

      {/* Blank overlay when the screen bus is paused (CB-143). Above the
          background signage (z-10) so a paused web page / menu / still image
          goes black, but below live takeovers (z-20) and the autoplay prompt
          (z-30) so those still surface. */}
      {screenPaused && (
        <div
          className="absolute inset-0 z-[15] bg-black"
          data-testid="screen-paused-blank"
          aria-hidden="true"
        />
      )}

      {foregroundVideoItem && (
        <SignageCrossfade
          item={foregroundVideoItem}
          setVideoRef={setForegroundVideoElement}
          muted={foregroundVideoMuted}
          className={`absolute inset-0 size-full ${fitClass} z-20`}
          surface="foreground"
          onImageError={() => reportScreenImageError("foreground")}
        />
      )}

      {autoplayBlocked && (
        <button
          type="button"
          className="absolute inset-0 z-30 flex flex-col items-center justify-center gap-2 bg-background/90 cursor-pointer px-6 text-center"
          onClick={onAutoplayStart}
          data-testid="button-autoplay-start"
          aria-label={t("playerUi.autoplay.tapToStartAria")}
        >
          <span className="text-sm font-medium text-foreground">
            {stallWarning ?? t("playerUi.autoplay.tapToStart")}
          </span>
          {!stallWarning && (
            <span className="text-xs text-muted-foreground">
              {t("playerUi.autoplay.hint")}
            </span>
          )}
        </button>
      )}

      <PlayerDebugPanel
        sync={sync}
        current={current}
        isPlaying={isPlaying}
        scheduleEnabled={scheduleEnabled}
        audioSnapshot={playerDebug.audioSnapshot}
        audioContextState={playerDebug.audioContextState}
        stallWarning={playerDebug.stallWarning}
      />
    </div>
  );
}

/**
 * The live now-playing surface. Kiosk players render a blank canvas (video fills
 * the screen when scheduled); operator transport is remote-only unless policy
 * unlocks on-device controls.
 */
export default function PlayerPage() {
  const { device, credential } = useDevice();
  const { requestSwitchMode, forceQuit, forceLogout } = useKioskExit();
  const { sync, refetch: refetchSync } = useDeviceSync(Boolean(credential));

  // Keep the kiosk screen (and therefore this tab's JS event loop) awake: a
  // dozed tab still answers WebSocket pongs at the protocol level, so the
  // gateway keeps reporting the device connected while commands sit
  // unexecuted. See use-wake-lock.ts for the full rationale.
  useWakeLock(Boolean(credential));

  const playbackRef = useRef<Playback | null>(null);
  const handleCommand = useCallback<CommandHandler>(
    (cmd) => {
      const pb = playbackRef.current;
      if (!pb) return { ok: false, result: { error: "player not ready" } };
      return applyPlayerCommand(pb, cmd, {
        forceSync: refetchSync,
        quit: forceQuit,
        logout: forceLogout,
      });
    },
    [refetchSync, forceQuit, forceLogout],
  );

  const signalHandlerRef = useRef<((sig: SignalMessage) => void) | null>(null);
  const handleSignal = useCallback((sig: SignalMessage) => {
    signalHandlerRef.current?.(sig);
  }, []);

  const broadcastRef = useRef<{ handleBroadcastEnded: () => void } | null>(null);
  const handleBroadcastEnded = useCallback(() => {
    broadcastRef.current?.handleBroadcastEnded();
  }, []);

  const {
    connected,
    sendNowPlaying,
    sendStatus,
    sendSignal,
    sendDeviceMetric,
    sendCacheReport,
  } = usePlayerRealtime({
    token: credential,
    onCommand: handleCommand,
    onSignal: handleSignal,
    onBroadcastEnded: handleBroadcastEnded,
  });

  const connectedRef = useRef(connected);
  connectedRef.current = connected;

  /**
   * Forward offline-cache readiness from the sync cycle to the gateway.
   *
   * The gate keeps this to roughly two frames an hour. Only the desktop shell
   * ever gets here: use-device-sync dispatches the event behind
   * isOfflineCapablePlayer(), so a plain browser tab is online-only by design
   * and its server-side offline_cache column correctly stays NULL.
   */
  const cacheReportMemoRef = useRef<CacheReportMemo | null>(null);
  useEffect(() => {
    const onCacheReport = (event: Event) => {
      const report = (event as CustomEvent<OfflineReadinessReport>).detail;
      if (!report) return;
      const now = Date.now();
      if (
        !shouldSendCacheReport({ report, last: cacheReportMemoRef.current, now })
      ) {
        return;
      }
      sendCacheReport({ ...report, reportedAt: new Date(now).toISOString() });
      cacheReportMemoRef.current = cacheReportMemo(report, now);
    };
    window.addEventListener(CACHE_REPORT_EVENT, onCacheReport);
    return () => window.removeEventListener(CACHE_REPORT_EVENT, onCacheReport);
  }, [sendCacheReport]);
  const handleNowPlaying = useCallback(
    (payload: NowPlayingPayload) => {
      if (connectedRef.current) {
        sendNowPlaying(payload);
      } else if (isOfflineCapablePlayer() && payload.state === "playing") {
        void enqueueEvent(payload);
      }
    },
    [sendNowPlaying],
  );

  useEffect(() => {
    if (!isOfflineCapablePlayer()) return;
    if (!connected) return;
    void flushTelemetryQueue();
  }, [connected]);

  useEffect(() => {
    if (!isOfflineCapablePlayer()) return;
    const onOnline = () => {
      void flushTelemetryQueue();
    };
    window.addEventListener("online", onOnline);
    return () => window.removeEventListener("online", onOnline);
  }, []);

  // Ask the browser/shell for durable storage so the offline mirror (IndexedDB
  // schedule + Cache Storage media) is never evicted under storage pressure.
  useEffect(() => {
    if (!isOfflineCapablePlayer()) return;
    void navigator.storage?.persist?.().catch(() => {
      // Best-effort; playback continues either way.
    });
  }, []);

  const playback = usePlayback({
    sync,
    onNowPlaying: handleNowPlaying,
    onDeviceMetric: sendDeviceMetric,
    onStatus: sendStatus,
  });
  playbackRef.current = playback;

  // Device settings live on the sync payload (kept fresh by resyncs); fall
  // back to /device/me for the window before the first sync arrives. Asset
  // tones use the server-hydrated presigned URL riding the same payloads.
  const deviceSettings = sync?.device?.settings ?? device?.settings;
  const hydratedToneUrl =
    sync?.device?.announcementToneUrl ?? device?.announcementToneUrl ?? null;
  const fitClass = videoFitClass(readVideoFit(deviceSettings));
  const orientation = readOrientation(deviceSettings);
  const scalePercent = readScale(deviceSettings);

  const broadcast = useBroadcastReceiver({
    sendSignal,
    setBroadcastActive: playback.setBroadcastActive,
    announcementToneUrl: resolveToneUrl(deviceSettings, hydratedToneUrl),
    deviceVolume: playback.muted ? 0 : playback.volume,
  });
  signalHandlerRef.current = broadcast.handleSignal;
  broadcastRef.current = broadcast;

  const {
    current,
    upNext,
    scheduleEnabled,
    isPlaying,
    autoplayBlocked,
    videoItem,
    nextVideoItem,
    signageContent,
    foregroundVideoItem,
    foregroundVideoMuted,
    screenPaused,
    play,
    setVideoElement,
    setForegroundVideoElement,
    reportScreenImageError,
  } = playback;

  const { playerDebug } = playback;

  const kioskLocked = onDeviceControlsLocked(device?.settings);
  // The secret exit gesture works on every player (kiosk or not) — it is the
  // uniform "get me out" seam; requestSwitchMode PIN-guards it only when the
  // device is kiosk-locked with a PIN set.
  useKioskSecretExit(requestSwitchMode, Boolean(device));

  // On the desktop shell the player window is ALWAYS fullscreen; drive its
  // kiosk LOCK from the device's kioskMode setting so the web fleet config
  // decides whether the box is locked in (CB-153). Reporting `false` also
  // tells the shell to (re)open the hub at this device's control page.
  useEffect(() => {
    if (!isDesktopShell() || !isPlayerSurface()) return;
    if (!device) return;
    // device.id rides along so the shell can backfill WHICH device this box
    // runs (installs provisioned before v0.1.4 never recorded it) — the hub
    // relaunch-restore depends on it (CB-169).
    void window.omnimediaDesktop?.setKiosk?.(kioskLocked, device.id);
  }, [kioskLocked, device]);

  if (!device) {
    return (
      <div className="min-h-[100dvh] flex items-center justify-center bg-background">
        <Loader2 className="size-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // The player surface is ALWAYS the pure full-page player (CB-166): it is the
  // venue's screen, and nothing may overlap the content — no headers, badges or
  // transport. Kiosk mode changes exactly ONE thing: whether the surface is
  // LOCKED (desktop window lock via setKiosk above, the route trap, the
  // PIN-guarded exit). When kiosk is off you leave it the platform-native way —
  // close the window / navigate away — and the secret exit gesture also works.
  // Operating the device (transport, volume, status, offline readiness) belongs
  // to the CONTROL surfaces, never to the player.
  return (
    <>
      <DisplayTransform orientation={orientation} scalePercent={scalePercent}>
        <KioskPlayerShell
          sync={sync}
          current={current}
          upNext={upNext}
          isPlaying={isPlaying}
          scheduleEnabled={scheduleEnabled}
          videoItem={videoItem}
          signageContent={signageContent}
          foregroundVideoItem={foregroundVideoItem}
          foregroundVideoMuted={foregroundVideoMuted}
          screenPaused={screenPaused}
          fitClass={fitClass}
          autoplayBlocked={autoplayBlocked}
          stallWarning={playerDebug.stallWarning}
          playerDebug={playerDebug}
          setVideoElement={setVideoElement}
          setForegroundVideoElement={setForegroundVideoElement}
          reportScreenImageError={reportScreenImageError}
          onAutoplayStart={play}
        />
      </DisplayTransform>
      <SignagePreloader next={nextVideoItem} current={videoItem} />
      <PlayerDebugPanel
        sync={sync}
        current={current}
        isPlaying={isPlaying}
        scheduleEnabled={scheduleEnabled}
        audioSnapshot={playerDebug.audioSnapshot}
        audioContextState={playerDebug.audioContextState}
        stallWarning={playerDebug.stallWarning}
      />
    </>
  );
}
